import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { fleetAdminAPI } from '../services/api';

interface FleetClientRow {
  customer_id: string;
  razon_social: string;
  fleet_enabled: boolean;
  agent_count: number;
  user_count: number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const FleetClients: React.FC = () => {
  const [clients, setClients] = useState<FleetClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Search, filter, pagination state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadClients = async () => {
    setLoading(true);
    setEndpointUnavailable(false);
    try {
      const data = await fleetAdminAPI.getFleetClients();
      setClients(data);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 501 || status === 422) {
        setEndpointUnavailable(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Error al cargar clientes fleet',
          severity: 'error',
        });
        setEndpointUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  // Filtered and searched clients
  const filteredClients = useMemo(() => {
    let result = clients;

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter((c) => c.fleet_enabled);
    } else if (statusFilter === 'inactive') {
      result = result.filter((c) => !c.fleet_enabled);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.customer_id.toLowerCase().includes(q) ||
          (c.razon_social || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [clients, search, statusFilter]);

  // Paginated slice
  const paginatedClients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredClients.slice(start, start + rowsPerPage);
  }, [filteredClients, page, rowsPerPage]);

  const handleToggleFleet = async (_clientId: string) => {
    setSnackbar({
      open: true,
      message:
        'Endpoint no disponible — se implementará en una próxima actualización',
      severity: 'info',
    });
  };

  const activeCount = clients.filter((c) => c.fleet_enabled).length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Fleet - Clientes
        </Typography>
        <Chip
          label={`${activeCount} activos de ${clients.length}`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {endpointUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Endpoint no disponible — se implementará en una próxima actualización
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por código o nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={statusFilter}
            label="Estado"
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activos</MenuItem>
            <MenuItem value="inactive">Inactivos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Customer ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Razón Social</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Estado Fleet</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                # Agentes
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                # Usuarios
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Cargando...
                </TableCell>
              </TableRow>
            ) : paginatedClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  {endpointUnavailable
                    ? 'El endpoint de clientes fleet aún no está disponible.'
                    : search || statusFilter !== 'all'
                    ? 'No se encontraron clientes con los filtros seleccionados.'
                    : 'No hay clientes registrados.'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedClients.map((client) => (
                <TableRow key={client.customer_id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {client.customer_id}
                  </TableCell>
                  <TableCell>{client.razon_social}</TableCell>
                  <TableCell>
                    <Chip
                      label={client.fleet_enabled ? 'Activo' : 'Inactivo'}
                      color={client.fleet_enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {client.agent_count ?? 0}
                  </TableCell>
                  <TableCell align="center">
                    {client.user_count ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        client.fleet_enabled
                          ? 'Desactivar Fleet'
                          : 'Activar Fleet'
                      }
                    >
                      <IconButton
                        onClick={() => handleToggleFleet(client.customer_id)}
                        color={client.fleet_enabled ? 'error' : 'success'}
                        size="small"
                      >
                        {client.fleet_enabled ? (
                          <ToggleOnIcon />
                        ) : (
                          <ToggleOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {!loading && filteredClients.length > 0 && (
          <TablePagination
            component="div"
            count={filteredClients.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        )}
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FleetClients;
